import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, IconButton, Typography } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import NotesIcon from '@material-ui/icons/Notes';
import moment from 'moment';

import { LeaderboardConfig } from 'api';
import { PaddedPaper } from 'components';
import LeaderboardPointDialog from './LeaderboardPointDialog';

interface IProps extends WithStyles<typeof styles> {
  config: LeaderboardConfig;
}

const styles = (theme: Theme) =>
  createStyles({
    description: {
      marginTop: 12,
      fontWeight: theme.typography.fontWeightRegular,
    },
    lowerCase: {
      textTransform: 'lowercase',
    },
  });

const LeaderboardHeader: FC<IProps> = ({ classes, config }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { all_users, start_at, end_at, title, description } = config;

  function renderTimeframe() {
    if (all_users) {
      return <Typography>{t('leaderboard:timeframe')}</Typography>;
    }

    const startDate = !!start_at && moment.unix(start_at).format('ll');
    const endDate = !!end_at && moment.unix(end_at).format('ll');
    const startAndEnd = !!start_at && !!end_at;

    if (!startDate && !endDate) {
      return <Typography>{t('common:ongoing')}</Typography>;
    }

    if (startAndEnd) {
      return (
        <Typography>
          {startDate} <span className={classes.lowerCase}>{t('common:to')}</span> {endDate}
        </Typography>
      );
    }

    if (startDate) {
      return <Typography>{t('leaderboard:startDate', { startDate })}</Typography>;
    }

    const hasEnded = moment().isAfter(moment(end_at));
    const i18nKey = hasEnded ? 'leaderboard:endedDate' : 'leaderboard:endsDate';

    return <Typography>{t(i18nKey, { endDate })}</Typography>;
  }

  return (
    <Fragment>
      <LeaderboardPointDialog config={config} open={open} onClose={() => setOpen(false)} />
      <PaddedPaper>
        <Grid container alignItems="center">
          <Grid item xs>
            <Typography variant="subtitle2">{title}</Typography>
          </Grid>
          <Grid item>
            <Grid container alignItems="center" spacing={3}>
              <Grid item>{renderTimeframe()}</Grid>
              <Grid item>
                <IconButton onClick={() => setOpen(true)}>
                  <NotesIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {!!description && (
          <Grid container>
            <Grid item>
              <Typography className={classes.description} variant="subtitle2">
                {description}
              </Typography>
            </Grid>
          </Grid>
        )}
      </PaddedPaper>
    </Fragment>
  );
};

export default withStyles(styles)(LeaderboardHeader);
